/* ══════════════════════════════════════════════════════════════════════
   DETAILS — the access-review table under the graph.

   One row per account of the selected application, for the selected
   review cycle. Independent of the graph selection by design: it
   reflects the top filter bar only.

   The policy counts are real (derived from the access payload). The
   campaign columns and the pending/completed split are mocked — see
   PAG_REVIEW_META in ../data/pagMockData.
   ══════════════════════════════════════════════════════════════════════ */
import { useCallback, useEffect, useMemo, useState } from "react";
import { FileDown, Search, ShieldUser, X } from "lucide-react";
import { DataTable, type Column } from "../../../components/DataTable";
import { TABLE_PAGE_SIZE_OPTIONS, safeFormatDate } from "../../../../utils/formatters";
import { PagDrilldownModal } from "./PagDrilldownModal";
import { completedCount } from "../data/pagPreviousCycle";
import {
  fetchPagCurrentCycleList,
  fetchPagPreviousCycleList,
  type PagCycleListResponse,
  type PagDrilldownStatus,
} from "../../../../api/frameworksApi";
import {
  PAG_BREAKDOWN_SEGMENTS,
  type PagCycle,
  type PagPolicyBreakdown,
  type PagReviewRow,
} from "../data/pagTypes";

/* ══════════════════════════════════════════════════════════════════════
   TYPOGRAPHY SCALE

   Deliberately small, and built on the sizes DataTable already sets for
   every table in the app — 13px table base, 11px supporting detail — so
   this table reads as part of the same system rather than a new one.
   Hierarchy comes from weight and colour at those two sizes, not from
   inventing extra font sizes.

   ONE rule decides weight, so no column looks arbitrarily heavier than
   its neighbour: TEXT is normal, FIGURES are semibold. The drill-down
   counts are already set apart by being indigo links, so weight never has
   to do that job as well.

     value    every text cell — the bulk of the table
     strong   every figure (pending / completed / totals)
     meta     supporting detail; must never compete with the line above
     primary  reserved for headings outside the grid

   The table header needs nothing here: DataTable already renders it at
   text-[13px] font-medium, which is the intended header treatment.
   ══════════════════════════════════════════════════════════════════════ */
const TEXT = {
  primary: "text-[13px] font-semibold leading-5 text-[#1e293b] dark:text-white",
  value: "text-[13px] font-normal leading-5 text-[#334155] dark:text-white/80",
  meta: "text-[11px] font-normal leading-4 text-[#94a3b8] dark:text-white/40",
  strong:
    "text-[13px] font-semibold leading-5 tabular-nums text-[#1e293b] dark:text-white",
} as const;

/* DataTable defaults cells to align-top, which leaves one-line values
   stranded at the top of the taller rows the donut and reviewer stack
   create. Tailwind's own align-top wins on stylesheet order at equal
   specificity, so this needs v4's trailing `!` to take effect. */
const CELL = "align-middle!";

/* ── Column widths ──────────────────────────────────────────────────────
   Without explicit widths DataTable runs `table-auto` with break-words,
   which snapped values mid-token — "DevSecO / ps & Infra", "CloudCostMast
   / er". Declaring a width per column switches it to table-fixed, and the
   single-line columns then clamp with an ellipsis instead of breaking.
   Reviewers and Policy Count are deliberately left unclamped: one stacks
   names, the other holds the donut. */
const W = {
  application: 145,
  criticality: 100,
  department: 145,
  /* Wide enough for "DevSecOps & Infra", the longest real value — it fell
     two characters short of fitting and clipped for no good reason. */
  division: 178,
  account: 180,
  reviewCycle: 125,
  dueDate: 120,
  reviewers: 155,
  policyCount: 225,
  pending: 100,
  completed: 110,
  status: 165,
} as const;

const px = (n: number) => `${n}px`;

/* Alignment follows the UAR page this table replaces, so the two read as
   the same grid rather than two takes on it:

     headers  centred, every column without exception
     text     left — names, criticality, department, division, reviewers
     figures  centred — review cycle, the date, the counts and the donut

   What went wrong before was not the rule but the gaps in it: columns that
   set nothing fell through to DataTable's centred default and sat between
   columns that had asked for left, so the grid looked crooked. Every
   column now spreads one of these two, and neither has a default to fall
   through to.

   Clamping is gone with them. Columns used to cut off at one line, which
   hid the ends of longer account and application names; values wrap now.
   The widths stay, because they are what holds the table in table-fixed
   and stops `table-auto` snapping values mid-token. */
const HEADER = { headerAlign: "center", cellClassName: CELL } as const;
const TEXT_COL = { ...HEADER, align: "left" } as const;
const FIGURE_COL = { ...HEADER, align: "center" } as const;

const CRITICALITY_BADGE: Record<
  string,
  { label: string; className: string }
> = {
  LOW: {
    label: "Low",
    className:
      "bg-blue-500/20 text-blue-400 dark:bg-blue-500/20 dark:text-blue-400",
  },
  MEDIUM: {
    label: "Medium",
    className:
      "bg-yellow-500/20 text-yellow-400 dark:bg-yellow-500/20 dark:text-yellow-400",
  },
  HIGH: {
    label: "High",
    className:
      "bg-orange-500/20 text-orange-400 dark:bg-orange-500/20 dark:text-orange-400",
  },
  CRITICAL: {
    label: "Critical",
    className:
      "bg-red-500/20 text-red-400 dark:bg-red-500/20 dark:text-red-400",
  },
};

const normalizeCriticality = (value: string) =>
  value.trim().replace(/\s+/g, "_").replace(/-/g, "_").toUpperCase();

function CriticalityBadge({ value }: { value: string }) {
  const meta = CRITICALITY_BADGE[normalizeCriticality(value)];

  /* An unmapped value still must not shout. The API sends "MEDIUM" and the
     map Title-cases it; anything it does not know was falling through in
     whatever case it arrived, which is how a raw ALL CAPS value reached
     the column. */
  if (!meta) {
    const raw = String(value).trim();
    return (
      <span className={TEXT.value}>
        {raw ? raw.charAt(0).toUpperCase() + raw.slice(1).toLowerCase() : value}
      </span>
    );
  }

  return (
    <span
      className={`inline-flex h-[18px] w-[88px] items-center justify-center rounded-full text-[11px] font-bold leading-none ${meta.className}`}
    >
      {meta.label}
    </span>
  );
}

/** A count that drills down. Zero is not actionable, so it renders as a
 *  plain figure rather than a link that opens an empty list. */
function CountLink({ value, onClick }: { value: number; onClick: () => void }) {
  if (!value) return <span className={TEXT.strong}>{value}</span>;
  return (
    <button
      onClick={onClick}
      className="rounded text-[13px] font-semibold leading-5 tabular-nums text-indigo-600 underline underline-offset-4 transition-colors hover:text-indigo-500 dark:text-indigo-400"
    >
      {value}
    </button>
  );
}

/* ── Donut ──────────────────────────────────────────────────────────── */

const DONUT_SIZE = 58;
const DONUT_THICKNESS = 8;

/** Policy-count donut: one arc per identity kind, total in the middle.
 *  Hand-rolled SVG rather than a chart library — at this size a full
 *  ResponsiveContainer per row costs far more than four <circle>s. */
function PolicyCountDonut({ breakdown }: { breakdown: PagPolicyBreakdown }) {
  const radius = (DONUT_SIZE - DONUT_THICKNESS) / 2;
  const circumference = 2 * Math.PI * radius;
  const center = DONUT_SIZE / 2;

  let consumed = 0;
  const arcs = PAG_BREAKDOWN_SEGMENTS.map((seg) => {
    const value = breakdown[seg.key];
    if (!value || !breakdown.total) return null;
    const length = (value / breakdown.total) * circumference;
    const arc = (
      <circle
        key={seg.key}
        cx={center}
        cy={center}
        r={radius}
        fill="none"
        stroke={seg.color}
        strokeWidth={DONUT_THICKNESS}
        strokeDasharray={`${length} ${circumference - length}`}
        strokeDashoffset={-consumed}
      />
    );
    consumed += length;
    return arc;
  }).filter(Boolean);

  return (
    <svg
      width={DONUT_SIZE}
      height={DONUT_SIZE}
      viewBox={`0 0 ${DONUT_SIZE} ${DONUT_SIZE}`}
      className="flex-shrink-0"
      role="img"
      aria-label={`${breakdown.total} policies`}
    >
      {/* Track — also the whole ring when an account has no policies */}
      <circle
        cx={center}
        cy={center}
        r={radius}
        fill="none"
        strokeWidth={DONUT_THICKNESS}
        className="stroke-muted"
      />
      <g transform={`rotate(-90 ${center} ${center})`}>{arcs}</g>
      <text
        x="50%"
        y="50%"
        textAnchor="middle"
        dominantBaseline="central"
        className="fill-[#1e293b] dark:fill-white"
        style={{ fontSize: 15, fontWeight: 600 }}
      >
        {breakdown.total}
      </text>
    </svg>
  );
}

function PolicyCountCell({ breakdown }: { breakdown: PagPolicyBreakdown }) {
  return (
    <div className="flex items-center gap-3">
      <PolicyCountDonut breakdown={breakdown} />
      {/* The category is the secondary read here and the figure the primary
          one, so the label stays at normal weight and only the number is
          emphasised. Colour still ties each line to its arc. */}
      <div className="flex flex-col gap-[3px]">
        {PAG_BREAKDOWN_SEGMENTS.map((seg) => (
          <span
            key={seg.key}
            className="whitespace-nowrap text-[11px] font-normal leading-4"
            style={{ color: seg.color }}
          >
            {seg.label} - <span className="font-semibold">{breakdown[seg.key]}</span>
          </span>
        ))}
      </div>
    </div>
  );
}

/* ── Report export ──────────────────────────────────────────────────── */

const CSV_HEADERS = [
  "Application Name",
  "Criticality",
  "Department",
  "Division",
  "Account Name",
  "Account ID",
  "Review Cycle",
  "Due Date",
  "Reviewers",
  "Policy Count",
  "Groups",
  "Roles",
  "Users",
  "Pending",
  "Completed",
];

const csvCell = (v: string | number) => `"${String(v).replace(/"/g, '""')}"`;

function downloadReport(rows: PagReviewRow[], cycle: PagCycle) {
  const body = rows.map((r) =>
    [
      r.applicationName,
      r.criticality,
      r.department,
      r.division,
      r.accountName,
      r.accountId,
      r.reviewCycle,
      safeFormatDate(r.dueDate),
      r.reviewers.join("; "),
      r.policyCount.total,
      r.policyCount.groups,
      r.policyCount.roles,
      r.policyCount.users,
      r.pending,
      r.completed,
    ]
      .map(csvCell)
      .join(","),
  );

  const csv = [CSV_HEADERS.map(csvCell).join(","), ...body].join("\r\n");
  const url = URL.createObjectURL(new Blob([csv], { type: "text/csv;charset=utf-8;" }));
  const a = document.createElement("a");
  a.href = url;
  a.download = `privilege-access-review-${cycle}-cycle.csv`;
  a.click();
  URL.revokeObjectURL(url);
}

/* ── Main ───────────────────────────────────────────────────────────── */

/** API payload -> table rows. Both cycles return the identical shape.
 *  Nullable fields become "-" so an unscheduled account reads as absent
 *  rather than blank. */
function toRows(res: PagCycleListResponse | null | undefined): PagReviewRow[] {
  const dash = (v: string | null | undefined) => (v && v.trim() ? v : "-");
  return (res?.data ?? []).map((r) => {
    const done = completedCount(r);
    return {
      id: `${r.appCode}|${r.accountId}`,
      appCode: r.appCode,
      applicationName: r.appName,
      criticality: dash(r.criticality),
      department: dash(r.businessDepartment),
      division: dash(r.businessDivision),
      accountId: r.accountId,
      accountName: r.accountName,
      reviewCycle: dash(r.reviewCycle),
      /* Epoch seconds here, ISO on the current cycle — safeFormatDate takes
         either, so the column renders the same both ways. */
      dueDate: r.dueDate ? new Date(r.dueDate * 1000).toISOString() : "",
      reviewers: r.reviewers ?? [],
      policyCount: {
        groups: r.groupCount ?? 0,
        roles: r.roleCount ?? 0,
        serviceRoles: r.serviceCount ?? 0,
        users: r.userCount ?? 0,
        total: r.totalStatusCount ?? 0,
      },
      pending: r.pendingStatusCount ?? 0,
      completed: done,
    };
  });
}

export function PagReviewTable({
  appCodes,
  ready = true,
  cycle,
  onCycleChange,
}: {
  /** Application filter, sent as the list request's `appCodes`. */
  appCodes: string[];
  /** False while the filter bar is still resolving its default
   *  application. Nothing is requested until it flips, so the first list
   *  call carries that appCode instead of an empty list. */
  ready?: boolean;
  cycle: PagCycle;
  onCycleChange: (c: PagCycle) => void;
}) {
  const isPrevious = cycle === "previous";
  const [search, setSearch] = useState("");
  /** Debounced copy — both cycles send `keyword` to the server. */
  const [appliedSearch, setAppliedSearch] = useState("");
  const [page, setPage] = useState(0); // DataTable pages are 0-indexed
  const [pageSize, setPageSize] = useState(10);
  const [rows, setRows] = useState<PagReviewRow[]>([]);
  const [total, setTotal] = useState(0);
  /* Opens loading rather than empty: the first request is pending on the
     filter bar, and an empty table in the meantime reads as "no data". */
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  /** Which count was clicked, if any. Null keeps the modal closed. */
  const [drilldown, setDrilldown] = useState<{
    row: PagReviewRow;
    status: PagDrilldownStatus;
  } | null>(null);

  const openDrilldown = (row: PagReviewRow, status: PagDrilldownStatus) =>
    setDrilldown({ row, status });

  useEffect(() => {
    const t = setTimeout(() => {
      setAppliedSearch(search.trim());
      setPage(0);
    }, 350);
    return () => clearTimeout(t);
  }, [search]);

  /* Reset paging when the cycle or the application filter changes, so a
     page-3 position never carries into a smaller result set. */
  useEffect(() => setPage(0), [cycle, appCodes.join(",")]);

  /* Both cycles are server-paged and server-searched through the identical
     request body, so only the endpoint differs. */
  const loadList = useCallback(() => {
    if (!ready) return;
    let cancelled = false;
    const fetchList = isPrevious ? fetchPagPreviousCycleList : fetchPagCurrentCycleList;

    setLoading(true);
    setError(null);
    fetchList({ appCodes, keyword: appliedSearch }, page, pageSize)
      .then((res) => {
        if (cancelled) return;
        setRows(toRows(res));
        setTotal(res?.totalElements ?? 0);
      })
      .catch((e: unknown) => {
        if (cancelled) return;
        setRows([]);
        setTotal(0);
        setError(e instanceof Error ? e.message : "Could not load the review list");
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });
    return () => {
      cancelled = true;
    };
    // appCodes is an array literal from the caller — join it so a new array
    // with the same contents does not refire the request every render.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [ready, isPrevious, appCodes.join(","), appliedSearch, page, pageSize]);

  useEffect(() => loadList(), [loadList]);

  /* Clamp on read so a shrinking result set can never strand the user on
     an empty page, without an effect that fights their own paging. */
  const totalPages = Math.max(1, Math.ceil(total / pageSize));
  const safePage = Math.min(page, totalPages - 1);
  const pageRows = rows;

  const columns: Column<PagReviewRow>[] = [
    {
      key: "applicationName",
      header: "Application Name",
      width: px(W.application),
      ...TEXT_COL,
      render: (r) => <span className={TEXT.value}>{r.applicationName}</span>,
    },
    {
      key: "criticality",
      header: "Criticality",
      width: px(W.criticality),
      ...FIGURE_COL,
      render: (r) => <CriticalityBadge value={r.criticality} />,
    },
    {
      key: "department",
      header: "Department",
      width: px(W.department),
      ...TEXT_COL,
      render: (r) => <span className={TEXT.value}>{r.department}</span>,
    },
    {
      key: "division",
      header: "Division",
      width: px(W.division),
      ...TEXT_COL,
      render: (r) => <span className={TEXT.value}>{r.division}</span>,
    },
    {
      key: "accountName",
      header: "Account Name",
      width: px(W.account),
      ...TEXT_COL,
      /* Name only. The account id is still carried on the row and sent in
         the drill-down request body — it is just not shown here. */
      render: (r) => <span className={TEXT.value}>{r.accountName}</span>,
    },
    {
      key: "reviewCycle",
      header: "Review Cycle",
      width: px(W.reviewCycle),
      ...FIGURE_COL,
      render: (r) => <span className={TEXT.value}>{r.reviewCycle}</span>,
    },
    {
      key: "dueDate",
      /* A closed cycle reports when it finished, not when it was owed. */
      header: isPrevious ? "Completed On" : "Due Date",
      width: px(W.dueDate),
      ...FIGURE_COL,
      render: (r) => (
        <span className={`whitespace-nowrap tabular-nums ${TEXT.value}`}>
          {safeFormatDate(r.dueDate)}
        </span>
      ),
    },
    {
      key: "reviewers",
      header: "Reviewers",
      width: px(W.reviewers),
      ...TEXT_COL,
      /* One name per line rather than a wrapped comma list: names break
         mid-name otherwise, and a fixed 3px rhythm keeps a four-reviewer
         cell from looking airier than a one-reviewer cell. */
      render: (r) =>
        r.reviewers.length ? (
          <div className="flex flex-col gap-[3px]">
            {r.reviewers.map((name) => (
              <span key={name} className={`whitespace-nowrap ${TEXT.value}`}>
                {name}
              </span>
            ))}
          </div>
        ) : (
          <span className={TEXT.meta}>-</span>
        ),
    },
    {
      key: "policyCount",
      header: "Policy Count",
      width: px(W.policyCount),
      ...FIGURE_COL,
      render: (r) => <PolicyCountCell breakdown={r.policyCount} />,
    },
    /* Current cycle splits the work into two actionable counts; a closed
       cycle reports one settled outcome instead. */
    ...(isPrevious
      ? [
          {
            key: "status",
            header: "Status",
            width: px(W.status),
            ...FIGURE_COL,
            render: (r: PagReviewRow) => (
              <button
                onClick={() => openDrilldown(r, "completed")}
                className="rounded text-[13px] font-semibold leading-5 text-indigo-600 underline underline-offset-4 transition-colors hover:text-indigo-500 dark:text-indigo-400"
              >
                <span className="tabular-nums">
                  {r.completed}/{r.policyCount.total}
                </span>{" "}
                Completed
              </button>
            ),
          } satisfies Column<PagReviewRow>,
        ]
      : [
          {
            key: "pending",
            header: "Pending",
            width: px(W.pending),
            ...FIGURE_COL,
            render: (r: PagReviewRow) => (
              <CountLink value={r.pending} onClick={() => openDrilldown(r, "pending")} />
            ),
          } satisfies Column<PagReviewRow>,
          {
            key: "completed",
            header: "Completed",
            width: px(W.completed),
            ...FIGURE_COL,
            render: (r: PagReviewRow) => (
              <CountLink
                value={r.completed}
                onClick={() => openDrilldown(r, "completed")}
              />
            ),
          } satisfies Column<PagReviewRow>,
        ]),
  ];

  const tabClass = (active: boolean) =>
    `border-b-2 pb-1.5 text-sm transition-colors ${
      active
        ? "border-indigo-500 font-semibold text-foreground"
        : "border-transparent font-medium text-muted-foreground hover:text-foreground"
    }`;

  return (
    <div className="rounded-xl border border-border bg-card p-5 pt-4">
      <h3 className="mb-3 text-sm font-semibold text-foreground">Details</h3>

      <div className="mb-4 flex flex-col gap-3 border-b border-border sm:flex-row sm:items-end sm:justify-between">
        {/* Cycle tabs */}
        <div className="flex items-center gap-5">
          <button className={tabClass(cycle === "current")} onClick={() => onCycleChange("current")}>
            Current Cycle
          </button>
          <button
            className={tabClass(cycle === "previous")}
            onClick={() => onCycleChange("previous")}
          >
            Previous Cycle
          </button>
        </div>

        <div className="flex flex-wrap items-center gap-2 pb-2">
          <div className="relative w-[190px]">
            <input
              type="text"
              value={search}
              maxLength={120}
              onChange={(e) => {
                setSearch(e.target.value);
                setPage(0);
              }}
              placeholder="Search"
              className="h-[34px] w-full rounded-lg border border-border bg-background pl-3 pr-8 text-xs text-foreground outline-none transition-colors focus:border-indigo-500"
            />
            {search ? (
              <button
                onClick={() => setSearch("")}
                title="Clear search"
                aria-label="Clear search"
                className="absolute right-2.5 top-1/2 -translate-y-1/2 text-muted-foreground transition-colors hover:text-foreground"
              >
                <X className="size-3.5" />
              </button>
            ) : (
              <Search className="pointer-events-none absolute right-2.5 top-1/2 size-3.5 -translate-y-1/2 text-muted-foreground" />
            )}
          </div>

          <button
            onClick={() => downloadReport(pageRows, cycle)}
            disabled={!pageRows.length}
            className="inline-flex h-[34px] items-center gap-1.5 rounded-lg bg-indigo-600 px-3.5 text-sm font-semibold text-white transition-colors hover:bg-indigo-500 disabled:cursor-default disabled:opacity-40"
          >
            <FileDown className="size-3.5" />
            Generate Report
          </button>
        </div>
      </div>

      <DataTable
        columns={columns}
        data={pageRows}
        loading={loading}
        error={error}
        onRetry={loadList}
        rowKey={(r) => r.id}
        emptyText="No review records match the current filters."
        emptyIcon={<ShieldUser className="size-7" />}
        page={safePage}
        pageSize={pageSize}
        totalItems={total}
        onPageChange={setPage}
        pagerStyle="simple"
        pageSizeOptions={[...TABLE_PAGE_SIZE_OPTIONS]}
        onPageSizeChange={(size) => {
          setPageSize(size);
          setPage(0);
        }}
        itemLabel="records"
        responsive
      />

      {/* Mounted only while open so each drill-down starts clean rather than
          reusing the previous account's scope, paging and selection. */}
      {drilldown && (
        <PagDrilldownModal
          open
          onOpenChange={(o) => !o && setDrilldown(null)}
          accountId={drilldown.row.accountId}
          accountName={drilldown.row.accountName}
          appCode={drilldown.row.appCode}
          status={drilldown.status}
          cycle={cycle}
        />
      )}
    </div>
  );
}
